import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { MAX_RECORDING_SECONDS } from '../hooks/useVoiceRecorder';
import { RecordingIndicator } from './RecordingIndicator';
import { VoiceConsentDialog } from './VoiceConsentDialog';
import { VoiceInputButton } from './VoiceInputButton';
import styles from './VoiceInput.module.css';

interface VoiceInputProps {
  title: string;
  setTitle: (next: string) => void;
  /** Переход на BillingScreen — существующий механизм навигации (PARA-131). */
  onOpenBilling: () => void;
}

// PARA-132 (AD-6) — единственный экспорт фичи наружу: экраны задач подключают
// голосовой ввод только через него, ничего не зная про запись, биллинг и auth.
export function VoiceInput({
  title,
  setTitle,
  onOpenBilling,
}: VoiceInputProps): JSX.Element | null {
  const { t } = useTranslation('voice');
  const voice = useVoiceInput({ title, setTitle });

  // AC-E9: среда не умеет записывать — управляющего элемента нет вовсе.
  if (voice.buttonState === 'hidden') {
    return null;
  }

  const isGated = voice.buttonState === 'gated';

  return (
    <div className={styles.voiceInput} data-testid="voice-input">
      <div className={styles.row}>
        <VoiceInputButton
          state={voice.buttonState}
          onPress={isGated ? onOpenBilling : voice.toggleRecording}
        />
      </div>

      {/* PARA-135 (AC-17, AC-E7, AC-E9) — подсказка тарифа и откат вставки
          живут строкой НИЖЕ кнопки и тоже центрированы. Условный рендер
          обязателен: безусловный пустой <div> добавил бы 8px из gap
          контейнера .voiceInput и сдвинул форму (AC-E8). */}
      {(isGated || voice.canUndo) && (
        <div className={styles.meta}>
          {isGated && <p className={styles.hint}>{t('gatedHint')}</p>}
          {voice.canUndo && (
            <Button variant="ghost" onClick={voice.undo}>
              {t('undo')}
            </Button>
          )}
        </div>
      )}

      {voice.buttonState === 'recording' && (
        <RecordingIndicator
          elapsedSeconds={voice.elapsedSeconds}
          onStop={voice.toggleRecording}
          onCancel={voice.cancelRecording}
        />
      )}

      {/* AC-15: статус обработки, уведомление о лимите и любая ошибка
          объявляются в live-region, а не только показываются. */}
      <div className={styles.live} role="status" data-testid="voice-status">
        {voice.buttonState === 'processing' && <p className={styles.note}>{t('processing')}</p>}
        {/* Лимит длительности живёт в одной константе: тексты про «60 секунд»
            (limitNotice, errors.tooLong) получают её интерполяцией. */}
        {voice.hasReachedLimit && (
          <p className={styles.note}>{t('limitNotice', { seconds: MAX_RECORDING_SECONDS })}</p>
        )}
        {voice.failure !== null && (
          <p className={styles.error} data-testid="voice-error">
            {t(voice.failure.messageKey, { seconds: MAX_RECORDING_SECONDS })}
          </p>
        )}
      </div>

      {/* AC-E1: отказ по тарифу ведёт туда же, куда и превентивный gating. */}
      {voice.failure?.kind === 'plan' && (
        <Button variant="soft" fullWidth onClick={onOpenBilling}>
          {t('errors.planCta')}
        </Button>
      )}

      {voice.isConsentDialogOpen && (
        <VoiceConsentDialog
          isPending={voice.isConsentPending}
          onConfirm={voice.confirmConsent}
          onDismiss={voice.dismissConsent}
        />
      )}
    </div>
  );
}
